import {
  Badge,
  Box,
  Card,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, Check, X } from "tabler-icons-react";
import HeaderPage from "../../../components/HeaderPage";
import { useGetStudentActivityDetailQuery } from "../../../redux/features/eleveApi";
import {
  findMatchingChoiceIndex,
  getActivityType,
  isDiagnostic,
  looksLikeHtml,
  normalizeChoices,
  parseMathExpression,
} from "../utils/activityTypes";

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Rend un contenu de diagnostic : expression mathématique (tokens JSON),
// contenu HTML (éditeur Quill), ou texte brut.
function DiagnosticContent({ content, bg, emptyText = "Non renseignée" }) {
  if (content === null || content === undefined || content === "") {
    return (
      <Text size="sm" color="dimmed" italic>
        {emptyText}
      </Text>
    );
  }

  const wrapperStyle = {
    padding: ".5rem .75rem",
    backgroundColor: bg,
    borderRadius: 7,
  };

  const expression = parseMathExpression(content);
  if (expression) {
    return (
      <Box style={wrapperStyle}>
        <Text
          weight={600}
          size="md"
          style={{ fontFamily: "monospace", letterSpacing: 0.5 }}
        >
          {expression}
        </Text>
      </Box>
    );
  }

  if (looksLikeHtml(content)) {
    return (
      <div style={wrapperStyle} dangerouslySetInnerHTML={{ __html: content }} />
    );
  }

  return (
    <Box style={wrapperStyle}>
      <Text size="sm">{content?.toString()}</Text>
    </Box>
  );
}

function CorrectIcon({ isCorrect }) {
  return (
    <ThemeIcon
      color={isCorrect ? "teal" : "red"}
      variant="light"
      radius="xl"
      size="md"
    >
      {isCorrect ? <Check size={16} /> : <X size={16} />}
    </ThemeIcon>
  );
}

// Un vrai QCM : plusieurs propositions dont une seule est la bonne réponse.
function ChoicesList({ choices, correctResponse, studentResponse }) {
  // On repère la bonne réponse et la réponse de l'élève par indice, avec
  // tolérance sur les arrondis (la proposition peut être arrondie).
  const correctIndex = findMatchingChoiceIndex(choices, correctResponse);
  const studentIndex = findMatchingChoiceIndex(choices, studentResponse);

  return (
    <Stack spacing="xs" mt="xs">
      {choices.map((prop, i) => {
        const isCorrect = i === correctIndex;
        const isStudent = i === studentIndex;
        const isWrongStudent = isStudent && !isCorrect;

        let bg = "transparent";
        let borderColor = "#e9ecef";
        if (isCorrect) {
          bg = "rgba(18,184,134,0.10)";
          borderColor = "#12b886";
        } else if (isWrongStudent) {
          bg = "rgba(250,82,82,0.10)";
          borderColor = "#fa5252";
        }

        return (
          <Paper
            key={i}
            p="xs"
            radius="sm"
            style={{ backgroundColor: bg, border: `1px solid ${borderColor}` }}
          >
            <Group position="apart" noWrap>
              <Text size="sm" style={{ fontFamily: "monospace" }}>
                {prop}
              </Text>
              <Group spacing={6} noWrap>
                {isStudent && (
                  <Badge
                    size="xs"
                    color={isCorrect ? "teal" : "red"}
                    variant="light"
                  >
                    Élève
                  </Badge>
                )}
                {isCorrect && (
                  <Badge size="xs" color="teal" variant="filled">
                    Bonne réponse
                  </Badge>
                )}
              </Group>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}

// --- Feuille de devoir pour les diagnostics (Maths / Lecture) ---
function DiagnosticSheet({ questions }) {
  return (
    <Stack spacing="lg">
      {questions?.map((q, index) => {
        const choices = normalizeChoices(q?.choices);
        const correct = (q?.correct_response ?? "").toString().trim();
        // On n'affiche un vrai QCM que s'il y a plusieurs propositions
        // distinctes de la réponse attendue.
        const isQcm =
          choices.length > 1 ||
          (choices.length === 1 && choices[0] !== correct);

        return (
          <Card key={index} withBorder radius="md" p="lg" shadow="sm">
            <Group position="apart" mb="sm">
              <Group spacing="xs">
                <Badge color="blue" variant="filled" radius="sm">
                  Question {index + 1}
                </Badge>
                {typeof q?.note === "number" && (
                  <Badge color="grape" variant="light">
                    Note : {q.note}
                  </Badge>
                )}
              </Group>
              <CorrectIcon isCorrect={q?.is_correct} />
            </Group>

            <Text weight={600} size="sm" mb={6}>
              Énoncé
            </Text>
            <DiagnosticContent content={q?.question} bg="rgba(0,0,0,0.03)" />

            {isQcm && (
              <>
                <Text weight={600} size="sm" mt="md" mb={6}>
                  Propositions
                </Text>
                <ChoicesList
                  choices={choices}
                  correctResponse={q?.correct_response}
                  studentResponse={q?.student_response}
                />
              </>
            )}

            <Divider my="md" variant="dashed" />

            <Group grow align="flex-start">
              <Box>
                <Text weight={600} size="sm" mb={6} color="blue">
                  Réponse de l'élève
                </Text>
                <DiagnosticContent
                  content={q?.student_response}
                  bg="rgba(34,139,230,0.08)"
                />
              </Box>
              <Box>
                <Text weight={600} size="sm" mb={6} color="teal">
                  Bonne réponse
                </Text>
                <DiagnosticContent
                  content={q?.correct_response}
                  bg="rgba(18,184,134,0.08)"
                />
              </Box>
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
}

// --- Feuille de devoir pour les Quiz de leçon ---
function QuizSheet({ questions }) {
  return (
    <Stack spacing="lg">
      {questions?.map((q, index) => (
        <Card key={index} withBorder radius="md" p="lg" shadow="sm">
          <Group position="apart" mb="sm" noWrap align="flex-start">
            <Group spacing="xs" noWrap>
              <Badge color="teal" variant="filled" radius="sm">
                {index + 1}
              </Badge>
              <Text weight={600}>{q?.question}</Text>
            </Group>
            <CorrectIcon isCorrect={q?.is_correct} />
          </Group>

          <Stack spacing="xs" mt="sm">
            {q?.propositions?.map((prop, i) => {
              const isCorrect = prop === q?.correct_response;
              const isStudent = prop === q?.student_response;
              const isWrongStudent = isStudent && !isCorrect;

              let bg = "transparent";
              let borderColor = "#e9ecef";
              if (isCorrect) {
                bg = "rgba(18,184,134,0.10)";
                borderColor = "#12b886";
              } else if (isWrongStudent) {
                bg = "rgba(250,82,82,0.10)";
                borderColor = "#fa5252";
              }

              return (
                <Paper
                  key={i}
                  p="xs"
                  radius="sm"
                  style={{
                    backgroundColor: bg,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <Group position="apart" noWrap>
                    <Text size="sm">{prop}</Text>
                    <Group spacing={6} noWrap>
                      {isStudent && (
                        <Badge
                          size="xs"
                          color={isCorrect ? "teal" : "red"}
                          variant="light"
                        >
                          Élève
                        </Badge>
                      )}
                      {isCorrect && (
                        <Badge size="xs" color="teal" variant="filled">
                          Bonne réponse
                        </Badge>
                      )}
                    </Group>
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

// --- Feuille pour les corrections d'exercice ---
function ExerciseSheet({ problems }) {
  return (
    <Stack spacing="lg">
      {problems?.map((p, index) => (
        <Card key={index} withBorder radius="md" p="lg" shadow="sm">
          <Group spacing="xs" mb="sm">
            <Badge color="orange" variant="filled" radius="sm">
              Problème {index + 1}
            </Badge>
          </Group>
          <Text weight={600} size="sm" mb={6}>
            Énoncé
          </Text>
          <Paper p="xs" radius="sm" style={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
            <Text size="sm">{p?.problem}</Text>
          </Paper>
          <Text weight={600} size="sm" mt="md" mb={6} color="teal">
            Résultat
          </Text>
          <Paper
            p="xs"
            radius="sm"
            style={{ backgroundColor: "rgba(18,184,134,0.08)" }}
          >
            <Text size="sm" weight={500}>
              {p?.result}
            </Text>
          </Paper>
        </Card>
      ))}
    </Stack>
  );
}

function EleveActiviteDetailPage() {
  const params = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useGetStudentActivityDetailQuery(
    {
      studentId: parseInt(params.id),
      type: params.type,
      activityId: params.activityId,
    },
    { skip: !params.id || !params.type || !params.activityId },
  );

  const activity = data?.data;
  const typeConfig = getActivityType(activity?.type);

  const renderSheet = () => {
    if (!activity) return null;
    if (isDiagnostic(activity.type)) {
      return <DiagnosticSheet questions={activity.questions} />;
    }
    if (activity.type === "LESSON_QUIZ") {
      return <QuizSheet questions={activity.questions} />;
    }
    if (activity.type === "EXERCISE_CORRECTION") {
      return <ExerciseSheet problems={activity.problems} />;
    }
    return (
      <Text color="dimmed" italic>
        Type d'activité non pris en charge
      </Text>
    );
  };

  return (
    <Box>
      <HeaderPage
        navigation={navigate}
        title={activity?.title || "Détail de l'activité"}
      />

      <Divider my={20} />

      {isLoading ? (
        <Group position="center" py={40}>
          <Loader />
        </Group>
      ) : !activity ? (
        <Card withBorder radius="md" p="xl">
          <Text color="dimmed">Activité introuvable</Text>
        </Card>
      ) : (
        <>
          {/* En-tête de la feuille */}
          <Card withBorder radius="md" p="lg" mb={20} shadow="sm">
            <Group position="apart" align="flex-start">
              <Box>
                <Badge color={typeConfig.color} variant="light" mb={8}>
                  {typeConfig.label}
                </Badge>
                <Title order={3}>{activity.title}</Title>
                <Group spacing={6} mt={8}>
                  <Calendar size={15} color="gray" />
                  <Text size="sm" color="dimmed">
                    {formatDate(activity.date)}
                  </Text>
                </Group>
              </Box>
              {activity.note != null && (
                <Paper
                  withBorder
                  radius="md"
                  p="md"
                  style={{ textAlign: "center", minWidth: 110 }}
                >
                  <Text size="xs" color="dimmed" transform="uppercase">
                    Note obtenue
                  </Text>
                  <Text size={30} weight={800} color={typeConfig.color}>
                    {activity.note}
                  </Text>
                </Paper>
              )}
            </Group>
          </Card>

          {renderSheet()}
        </>
      )}
    </Box>
  );
}

export default EleveActiviteDetailPage;
